import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { track } from '@/lib/telemetry';
import { useWorkspaceStore } from '@/state/workspace.store';
import { useAuth } from '@/state/AuthContext';
import { isAdminRole } from '@/types/roles';
import { listWorkspaces } from '@/features/workspaces/api';
import * as workTasksApi from '@/features/work-management/workTasks.api';
import toast from 'react-hot-toast';
import { useAdminWorkspacesModalStore } from '@/stores/adminWorkspacesModalStore';
import { formatModKChord } from '@/utils/modKeyLabel';

type CommandGroup = 'navigation' | 'workspaces' | 'administration';

type Command = {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
  /** Palette section; workspace switchers must sit under Workspaces, not as top-level peers. */
  group?: CommandGroup;
};

const MODAL_ROOT_ID = 'modal-root';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const createTaskInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [createTaskMode, setCreateTaskMode] = useState(false);
  const [createTaskTitle, setCreateTaskTitle] = useState('');
  const navigate = useNavigate();
  const { activeWorkspaceId, setActiveWorkspace } = useWorkspaceStore();
  const { user } = useAuth();
  const isAdmin = user?.role ? isAdminRole(user.role) : false;
  const [workspaces, setWorkspaces] = useState<Array<{ id: string; name: string; slug?: string }>>([]);

  const location = useLocation();

  /** Project ID from route when on a project page (e.g. /projects/:id or /work/projects/:id/plan). */
  const activeProjectId = (() => {
    const m = location.pathname.match(/^\/projects\/([^/]+)/) ?? location.pathname.match(/^\/work\/projects\/([^/]+)/);
    return m?.[1] ?? null;
  })();

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
    setCreateTaskMode(false);
    setCreateTaskTitle('');
    track('ui.menu.close', { surface: 'cmdk' });
  }, []);

  const openCmd = useCallback(() => {
    setOpen(true);
    track('ui.cmdk.open');
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  // Global hotkey: Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = (e.key ?? "").toLowerCase();
      const mod = e.metaKey || e.ctrlKey;
      if (mod && k === 'k') {
        // Template Center owns ⌘K while open (see TemplateCenterModal capture listener).
        if (document.body.getAttribute('data-template-center-open') === 'true') {
          return;
        }
        e.preventDefault();
        if (open) close(); else openCmd();
      }
      if (open && k === 'escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, openCmd]);

  // Commands (role-gated could be added via props/context)
  const [commands, setCommands] = useState<Command[]>([
    // Phase 4.7: relabeled to point at the canonical /inbox surface;
    // /home is the retired router and must not be a navigation target.
    { id: 'inbox', label: 'Go to Inbox', hint: '/inbox', group: 'navigation', run: () => navigate('/inbox') },
  ]);

  // Register workspace navigation action dynamically
  useEffect(() => {
    if (activeWorkspaceId) {
      setCommands(prev => {
        const exists = prev.find(c => c.id === 'workspace.home');
        if (exists) return prev;
        return [...prev, {
          id: 'workspace.home',
          label: 'Go to workspace home',
          hint: `/workspaces/${activeWorkspaceId}`,
          group: 'workspaces',
          run: () => {
            navigate(`/workspaces/${activeWorkspaceId}`);
            close();
          }
        }];
      });
    } else {
      setCommands(prev => prev.filter(cmd => cmd.id !== 'workspace.home'));
    }
  }, [activeWorkspaceId, navigate, close]);

  // PROMPT 10: Load workspaces for switcher
  useEffect(() => {
    if (open && user) {
      listWorkspaces()
        .then(ws => setWorkspaces(ws))
        .catch(err => console.error('Failed to load workspaces:', err));
    }
  }, [open, user]);

  // PROMPT 10: Add workspace switch commands
  useEffect(() => {
    if (workspaces.length > 0) {
      setCommands(prev => {
        // Remove existing workspace switch commands
        const filtered = prev.filter(cmd => !cmd.id.startsWith('workspace.switch-'));
        // Add switch commands for each workspace
        const switchCommands: Command[] = workspaces.map(ws => ({
          id: `workspace.switch-${ws.id}`,
          label: `Switch to ${ws.name}`,
          hint: ws.slug ? `/w/${ws.slug}/home` : `/workspaces/${ws.id}`,
          group: 'workspaces' as const,
          run: () => {
            setActiveWorkspace(ws.id);
            navigate(ws.slug ? `/w/${ws.slug}/home` : `/workspaces/${ws.id}`, { replace: true });
            close();
          }
        }));
        return [...filtered, ...switchCommands];
      });
    } else {
      // Remove workspace switch commands if no workspaces
      setCommands(prev => prev.filter(cmd => !cmd.id.startsWith('workspace.switch-')));
    }
  }, [workspaces, navigate, setActiveWorkspace, close]);

  // Accept any RFC 4122 UUID (v4, v7, etc.) — not v4-only, so DB can use v7 later.
  const isValidProjectId = (id: string | null): id is string =>
    !!id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

  const submitCreateTask = useCallback(() => {
    if (!activeProjectId || !isValidProjectId(activeProjectId)) {
      toast.error('Select a project first');
      navigate('/projects');
      close();
      return;
    }
    const title = createTaskTitle.trim();
    if (!title) return;
    workTasksApi
      .createTask({ projectId: activeProjectId, title })
      .then(() => {
        toast.success('Task created');
        close();
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to create task';
        toast.error(msg);
      });
  }, [activeProjectId, createTaskTitle, navigate, close]);

  // Create Task: requires valid workspace (workTasksApi) and project context
  useEffect(() => {
    setCommands(prev => {
      const filtered = prev.filter(c => c.id !== 'create-task');
      return [
        ...filtered,
        {
          id: 'create-task',
          label: activeProjectId ? 'Create task in this project' : 'Select a project first',
          hint: activeProjectId ? undefined : 'Go to Projects',
          group: 'navigation',
          run: () => {},
        },
      ];
    });
  }, [activeProjectId]);

  // Add Admin commands if user is admin
  useEffect(() => {
    if (isAdmin) {
      setCommands(prev => {
        const adminCommands: Command[] = [
          { id: 'admin-overview', label: 'Go to Administration', hint: '/administration', group: 'administration', run: () => navigate('/administration') },
          { id: 'admin-users', label: 'Manage people', hint: '/administration/people', group: 'administration', run: () => navigate('/administration/people') },
          { id: 'admin-workspaces', label: 'Manage workspaces', hint: 'Opens workspace browser', group: 'administration', run: () => useAdminWorkspacesModalStore.getState().open() },
        ];
        // Remove existing admin commands and add new ones
        const filtered = prev.filter(cmd => !cmd.id.startsWith('admin-'));
        return [...filtered, ...adminCommands];
      });
    } else {
      // Remove admin commands if user is not admin
      setCommands(prev => prev.filter(cmd => !cmd.id.startsWith('admin-')));
    }
  }, [isAdmin, navigate]);

  const q = query.toLowerCase();

  const { sections, flatFiltered } = useMemo(() => {
    const filtered = commands.filter((c) => c.label.toLowerCase().includes(q));
    const nav = filtered.filter((c) => (c.group ?? 'navigation') === 'navigation');
    const ws = filtered.filter((c) => c.group === 'workspaces');
    const adm = filtered.filter((c) => c.group === 'administration');
    const sec: { title: string | null; items: Command[] }[] = [];
    if (nav.length) sec.push({ title: null, items: nav });
    if (ws.length) sec.push({ title: 'Workspaces', items: ws });
    if (adm.length) sec.push({ title: 'Administration', items: adm });
    return { sections: sec, flatFiltered: sec.flatMap((s) => s.items) };
  }, [commands, q]);

  useEffect(() => {
    if (flatFiltered.length === 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((i) => Math.min(i, flatFiltered.length - 1));
  }, [flatFiltered.length]);

  const onExecute = (cmd: Command) => {
    if (cmd.id === 'create-task') {
      if (!activeProjectId || !isValidProjectId(activeProjectId)) {
        toast.error('Select a project first');
        navigate('/projects');
        close();
        return;
      }
      track('ui.cmdk.execute', { id: cmd.id, label: cmd.label });
      setCreateTaskMode(true);
      setCreateTaskTitle('');
      setQuery('');
      setActiveIndex(0);
      requestAnimationFrame(() => createTaskInputRef.current?.focus());
      return;
    }
    track('ui.cmdk.execute', { id: cmd.id, label: cmd.label });
    cmd.run();
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (createTaskMode) return;
    if (!flatFiltered.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const ni = Math.min(activeIndex + 1, flatFiltered.length - 1);
      setActiveIndex(ni);
      queueMicrotask(() =>
        listRef.current?.querySelector(`[data-cmdk-idx="${ni}"]`)?.scrollIntoView({ block: 'nearest' }),
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const ni = Math.max(activeIndex - 1, 0);
      setActiveIndex(ni);
      queueMicrotask(() =>
        listRef.current?.querySelector(`[data-cmdk-idx="${ni}"]`)?.scrollIntoView({ block: 'nearest' }),
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatFiltered[activeIndex]) {
        onExecute(flatFiltered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  if (!open) return null;

  const modalRoot = document.getElementById(MODAL_ROOT_ID) || document.body;

  return createPortal(
    <div
      aria-modal="true"
      role="dialog"
      aria-label="Command Palette"
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/30 p-8"
      data-testid="cmdk-dialog"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="w-full max-w-xl rounded-lg bg-white p-2 shadow-xl outline-none dark:bg-neutral-900">
        <div className="flex items-center gap-2 border-b border-neutral-200 p-2 dark:border-neutral-800">
          <span className="text-xs text-neutral-500">{formatModKChord()}</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={onKeyDown}
            placeholder="Type a command…"
            aria-label="Search commands"
            className="w-full bg-transparent p-2 outline-none"
            data-testid="cmdk-input"
            disabled={createTaskMode}
          />
        </div>
        {createTaskMode && (
          <div className="border-b border-neutral-200 px-3 py-2 dark:border-neutral-800">
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-400">
              Task name
            </label>
            <input
              ref={createTaskInputRef}
              type="text"
              value={createTaskTitle}
              onChange={(e) => setCreateTaskTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitCreateTask();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setCreateTaskMode(false);
                  setCreateTaskTitle('');
                  inputRef.current?.focus();
                }
              }}
              placeholder="Enter task title…"
              className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 dark:border-neutral-600 dark:bg-neutral-800"
              data-testid="cmdk-create-task-input"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300"
                onClick={() => {
                  setCreateTaskMode(false);
                  setCreateTaskTitle('');
                  inputRef.current?.focus();
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                disabled={!createTaskTitle.trim()}
                onClick={() => submitCreateTask()}
              >
                Create
              </button>
            </div>
          </div>
        )}
        <ul
          ref={listRef}
          role="menu"
          aria-label="Command results"
          className="max-h-80 overflow-auto p-2"
          data-testid="cmdk-results"
        >
          {(() => {
            let row = 0;
            return sections.map((sec) => (
              <li key={sec.title ?? 'nav'} className="list-none" role="presentation">
                {sec.title ? (
                  <div
                    className="px-3 pb-1 pt-2 text-[11px] font-extrabold uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
                    role="presentation"
                    aria-hidden
                  >
                    {sec.title}
                  </div>
                ) : null}
                <ul role="group" aria-label={sec.title ?? 'Commands'} className="space-y-0.5">
                  {sec.items.map((cmd) => {
                    const i = row++;
                    return (
                      <li
                        key={cmd.id}
                        role="menuitem"
                        tabIndex={-1}
                        data-cmdk-idx={i}
                        aria-selected={i === activeIndex}
                        onMouseEnter={() => setActiveIndex(i)}
                        onClick={() => onExecute(cmd)}
                        className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 ${
                          i === activeIndex
                            ? 'bg-neutral-100 dark:bg-neutral-800'
                            : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
                        }`}
                        data-testid={
                          cmd.id === 'workspace.settings'
                            ? 'action-workspace-settings'
                            : cmd.id === 'admin-overview'
                              ? 'action-admin-overview'
                              : cmd.id === 'admin-users'
                                ? 'action-admin-users'
                                : cmd.id === 'admin-workspaces'
                                  ? 'action-admin-workspaces'
                                  : `cmdk-item-${cmd.id}`
                        }
                      >
                        <span className={sec.title === 'Workspaces' ? 'pl-1' : undefined}>{cmd.label}</span>
                        {cmd.hint && <span className="text-xs text-neutral-500">{cmd.hint}</span>}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ));
          })()}
          {!flatFiltered.length && (
            <li className="px-3 py-6 text-sm text-neutral-500" data-testid="cmdk-empty">
              No commands found
            </li>
          )}
        </ul>
      </div>
    </div>,
    modalRoot
  );
}
